import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  ViewChild,
  signal,
  computed,
  DOCUMENT, effect,
} from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

import { MatDrawerMode, MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { IrisMainMenuService } from '@iris/common/services/main-menu.service';
import { IrisHotkeysService } from '@iris/common/services/utils/iris-hotkeys.service';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { IrisLogoService } from './common/services/logo.service';
import { IrisGlobalNotificationService } from '@iris/common/modules/ui/modules/global-announcement-banner/services/global-notification.service';
import { IrisLocalStorageService } from '@iris/common/services/utils/iris-local-storage.service';
import { IrisGlobalNavigationMenuComponent } from './common/modules/global-navigation-menu/components/global-navigation-menu/global-navigation-menu.component';
import { IrisHeaderModule } from '@iris/modules/header/header.module';
import { GlobalAnnouncementBannerModule } from './common/modules/ui/modules/global-announcement-banner/global-announcement-banner.module';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { translateStrakitDict } from '@iris/common/utils/strakitTranslateDict.utils';
import { StrakitButtonPrefixDirective } from '@ngx-strakit/cdk';
import { StrakitButtonComponent } from '@ngx-strakit/cdk/controls/button';
import { IrisModules } from '@iris/common/models/IrisModulesEnums';
import { VersionCheckComponent } from '@iris/modules/version-check/version-check.component';
import { IrisEnvironmentService } from '@iris/common/services/environment.service';
import { IrisUserService } from '@iris/common/services/user.service';
import { compareIrisVersions } from '@iris/common/utils/versionComparison';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app',
  imports: [
    RouterOutlet,
    MatSidenavModule,
    IrisGlobalNavigationMenuComponent,
    IrisHeaderModule,
    GlobalAnnouncementBannerModule,
    StrakitButtonPrefixDirective,
    StrakitButtonComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements AfterViewInit {
  private readonly hotkeysService = inject(IrisHotkeysService); // just for service initialization
  private readonly logoService = inject(IrisLogoService);
  private readonly storage = inject(IrisLocalStorageService);
  protected readonly globalNotificationService = inject(IrisGlobalNotificationService);
  private readonly mainMenuService = inject(IrisMainMenuService);
  private readonly translateService = inject(TranslateService);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly irisEnvironmentService = inject(IrisEnvironmentService);
  private readonly dialog = inject(MatDialog);
  private readonly userService = inject(IrisUserService);

  @ViewChild('sidenav') sidenav: MatSidenav;

  printMode = false;
  fullscreenMode = false;
  sidenavMode: MatDrawerMode;
  isPinned: boolean;

  isInsideSystemConfiguration = signal(false);
  userHide = signal(false);
  showMessage = computed(() => this.isInsideSystemConfiguration() && !this.userHide());

  private _prevInside = false;

  translateWarning = '<b> 📣IMPORTANT INFO:📣</b><br>' +
    'After saving a label with translation, all missing translations will be added automatically. Existing labels will not be changed.';

  private isPageUnloading = false;
  versionMismatch = computed(() => {
    const jwtVersion = this.irisEnvironmentService.backendVersion;
    const frontendVersion = this.irisEnvironmentService.env?.version;
    return jwtVersion && (compareIrisVersions(frontendVersion, jwtVersion, true) !== 0);
  });

  constructor(){
    this.sidenavMode = this.storage.getItem('sidebar_mode') || 'over';
    this.isPinned = this.sidenavMode === 'side';
    this.mainMenuService.updatePinnedState(this.isPinned);
    translateStrakitDict(this.translateService);

    this.activatedRoute.queryParams.pipe(
      map(params => coerceBooleanProperty(params['print'])),
      tap(printMode => this.printMode = printMode),
      takeUntilDestroyed(),
    ).subscribe();
    this.logoService.loadBrands().pipe(
      switchMap(() => this.logoService.activeBrand$),
      tap((activeBrand) => this.logoService.appendWebIcons(activeBrand?.brand)),
      takeUntilDestroyed(),
    ).subscribe();
    this.globalNotificationService.getActive().pipe(
      takeUntilDestroyed(),
    ).subscribe();
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => {
        let current: ActivatedRoute | null = this.activatedRoute.root;

        while (current) {
          if (current.snapshot?.data?.['moduleCode'] === IrisModules.SYSTEMCONFIG) {
            return true;
          }
          current = current.firstChild;
        }

        return false;
      }),
      distinctUntilChanged(),
      tap(isInside => this.isInsideSystemConfiguration.set(isInside)),
      takeUntilDestroyed(),
    ).subscribe();

    effect(() => {
      const inside = this.isInsideSystemConfiguration();
      if (!this._prevInside && inside) {
        this.userHide.set(false);
      }
      this._prevInside = inside;
    });

    effect(() => {
      const versionMismatch = this.versionMismatch();
      const skipModal = sessionStorage.getItem('skipVersionModal') === 'true';
      if (versionMismatch && !skipModal) {
        this.dialog.open<VersionCheckComponent, void, void>(VersionCheckComponent);
      }
    });
  }

  ngAfterViewInit(): void {
    this.mainMenuService.sidenav = this.sidenav;
  }

  onSidenavModeChange(mode: MatDrawerMode): void {
    this.storage.setItem('sidebar_mode', mode);
  }

  @HostListener('window:beforeunload')
  unloadHandler(): void {
    if (this.isPageUnloading) {
      return;
    }
    this.isPageUnloading = true;
  }

  @HostListener('document:fullscreenchange')
  @HostListener('document:webkitfullscreenchange')
  @HostListener('document:mozfullscreenchange')
  @HostListener('document:MSFullscreenChange')
  fullscreenModeHandler(): void {
    this.fullscreenMode = !(!this.document.fullscreenElement && !this.document['webkitIsFullScreen'] && !this.document['mozFullScreen'] && !this.document['msFullscreenElement']);
  }

  @HostListener('document:dragover', ['$event'])
  @HostListener('document:drop', ['$event'])
  disableDragAndDrop(event: DragEvent): void {
    const target = event.target as HTMLElement;
    if (target.classList?.contains('droppable')) {
      event.stopPropagation();
      return;
    }
    event.dataTransfer.effectAllowed = 'none';
    event.dataTransfer.dropEffect = 'none';
    event.preventDefault();
  }

  hideMessage(): void {
    this.userHide.set(true);
  }
}
